import { useState } from 'react'
import toast from 'react-hot-toast'
import { Check, Copy, ShieldAlert } from 'lucide-react'
import { Button } from '@/components/ui/button'
import Modal from '@/components/ui/modal'
import { Input } from '@/components/ui/input'

interface WebhookSecretModalProps {
  url: string
  secret: string
  mode: 'created' | 'rotated'
  onClose: () => void
}

export function WebhookSecretModal({ url, secret, mode, onClose }: WebhookSecretModalProps) {
  const [copied, setCopied] = useState(false)

  async function copySecret() {
    try {
      await navigator.clipboard.writeText(secret)
      setCopied(true)
      toast.success('Secreto copiado')
    } catch {
      toast.error('No se pudo copiar. Selecciona el texto y cópialo manualmente.')
    }
  }

  return (
    <Modal isOpen onClose={onClose} title={mode === 'created' ? 'Destino agregado' : 'Secreto rotado'} size="md">
      <div className="space-y-4">
        <div className="flex items-start gap-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
          <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <p>
            Guarda este secreto en tu sistema ahora. Por seguridad no lo volveremos a mostrar; si lo pierdes,
            tendrás que rotarlo otra vez.
          </p>
        </div>

        <div>
          <p className="mb-1 text-sm font-medium text-gray-900">Destino</p>
          <p className="break-all text-sm text-gray-600">{url}</p>
        </div>

        <div>
          <label htmlFor="webhook-secret" className="mb-1 block text-sm font-medium text-gray-900">
            Secreto para verificar la firma
          </label>
          <div className="flex gap-2">
            <Input
              id="webhook-secret"
              value={secret}
              readOnly
              onFocus={(event) => event.currentTarget.select()}
              className="font-mono text-xs"
            />
            <Button type="button" variant="outline" onClick={copySecret} aria-label="Copiar secreto">
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              <span className="ml-1.5 hidden sm:inline">{copied ? 'Copiado' : 'Copiar'}</span>
            </Button>
          </div>
        </div>

        <p className="text-xs text-gray-500">
          Enviamos la firma en la cabecera <code className="rounded bg-gray-100 px-1">X-Iridia-Signature</code>{' '}
          con el formato <code className="rounded bg-gray-100 px-1">v1=&lt;hmac&gt;</code>. La guía de integración
          incluye ejemplos listos para Google Sheets, Zapier y Power Automate.
        </p>

        <div className="flex justify-end">
          <Button type="button" onClick={onClose}>
            Ya lo guardé
          </Button>
        </div>
      </div>
    </Modal>
  )
}
